import { useState, useRef, ReactElement, ChangeEvent, ReactNode } from 'react'
import styled from 'styled-components'
import { isEmpty } from 'lodash'

import { SpinnerIcon } from '@10xLabs/react-web/components/Icons/Spinner'
import { Label } from 'components/v2/Label'
import List from 'components/v2/List'
import { Input } from 'components/Input'
import EmptyState from 'components/v2/EmptyState'
import { Checkbox } from 'components/v2/Checkbox'
import { Tag } from 'components/v2/Tag'
import ResponsiveDropdown from 'components/ResponsiveDropdown'
import { ApolloError } from '@apollo/client'

const StyledSpinnerIcon = styled(SpinnerIcon)`
  fill: #5d5c70;
`

const StyledCheckbox = styled(Checkbox)`
  > div {
    padding: 10px 15px;
  }
`

const InputTagsWrapper = styled.div<{ hasValues?: boolean }>`
  border-bottom: solid 1px #9999bd;
  padding: 8px;
  padding-bottom: ${({ hasValues }): string => (hasValues ? '3px' : '8px')};
  cursor: text;
  background-color: #ffffff;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`

const StyledInput = styled(Input)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledInputTag = styled(Input)<{ placeholder?: string; value?: string }>`
  padding: 4px 0px;
  border: none;
  max-width: 100%;

  ${({ placeholder, value }): string | undefined => {
    if (placeholder && !value) {
      return `
        width: ${placeholder.length * 10 + 2}px;
        max-width: 100%;
      `
    }
  }}
`

const ResponsiveDropdownContent = styled(ResponsiveDropdown.Content)`
  max-height: 330px;
  overflow: auto;
`

interface ISearchCheckboxesV2Props<Entity> {
  onChange: (values: Entity[], pattern: string) => void
  label?: ReactNode
  values?: Entity[]
  items?: Entity[]
  loading?: boolean
  error?: ApolloError | string
  inputValue: string
  emptyMessage: string
  getItemLabel: (item: Entity) => string
  getTagLabel: (item: Entity) => string
  allOption: string
  searchPlaceholder: string
}

export const SearchCheckboxesV2 = <Entity extends { id: string }>({
  onChange,
  label,
  values = [],
  items = [],
  loading,
  error,
  inputValue,
  emptyMessage,
  getItemLabel,
  getTagLabel,
  allOption,
  searchPlaceholder = '',
  ...inputProps
}: ISearchCheckboxesV2Props<Entity>): ReactElement => {
  let dropdownContent

  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isOpen, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const toggleDropdown = (): void => {
    setOpen(!isOpen)
  }

  const handleClose = (): void => {
    setOpen(false)
  }

  const handleItemSelect = ({ target: { id } }: React.ChangeEvent<HTMLInputElement>): void => {
    const item = items.find((item) => item.id === id)
    const wasSelected = values.some((value) => value.id === id)

    if (wasSelected) {
      onChange(
        values.filter((value) => value.id !== id),
        searchValue
      )
    } else if (item) {
      onChange([...values, item], searchValue)
    }
  }

  if (loading) {
    dropdownContent = (
      <EmptyState.Container>
        <StyledSpinnerIcon />
      </EmptyState.Container>
    )
  } else if (error) {
    dropdownContent = error
  } else if (!isEmpty(items)) {
    dropdownContent = (
      <List.Container>
        {!searchValue && allOption && (
          <List.Item noPadding>
            <StyledCheckbox checked={isEmpty(values)} onChange={(): void => onChange([], searchValue)}>
              {allOption}
            </StyledCheckbox>
          </List.Item>
        )}
        {items.map((item): ReactElement => {
          const isChecked = values.some((value) => value.id === item.id)

          return (
            <List.Item key={item.id} noPadding>
              <StyledCheckbox id={item.id} checked={isChecked} onChange={handleItemSelect}>
                {getItemLabel(item)}
              </StyledCheckbox>
            </List.Item>
          )
        })}
      </List.Container>
    )
  } else {
    dropdownContent = (
      <EmptyState.Container>
        <div>{emptyMessage}</div>
      </EmptyState.Container>
    )
  }

  const handleSearchChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(value)
    onChange(values, value)
  }

  const handleTagDelete = (tagIndex: number): void => {
    const newValues = values.filter((_, index) => {
      return index !== tagIndex
    })
    onChange(newValues, searchValue)
  }

  return (
    <ResponsiveDropdown.Wrapper isOpen={isOpen} setOpen={setOpen}>
      <ResponsiveDropdown.Toggle>
        <label>
          {label && <Label>{label}</Label>}
          <StyledInput
            {...inputProps}
            ref={inputRef}
            autoComplete="off"
            value={inputValue}
            onChange={(): void => {
              // do nothing
            }}
            onClick={toggleDropdown}
            onFocus={(): void => inputRef.current?.blur()}
          />
        </label>
      </ResponsiveDropdown.Toggle>

      <ResponsiveDropdownContent>
        <ResponsiveDropdown.Close onClick={handleClose} />
        <InputTagsWrapper hasValues={values.length > 0}>
          {values.map((selectedItem, index): ReactElement => {
            return (
              <Tag key={index} onDelete={handleTagDelete.bind(this, index)}>
                {getTagLabel(selectedItem)}
              </Tag>
            )
          })}
          <StyledInputTag
            value={searchValue}
            placeholder={searchPlaceholder || ''}
            onChange={handleSearchChange}
            noBorder
            noPadding
            hasDynamicWidth
            autoFocus
          />
        </InputTagsWrapper>
        {dropdownContent}
      </ResponsiveDropdownContent>
    </ResponsiveDropdown.Wrapper>
  )
}
